// @AddIntl

import { langParams, Locale } from '@repo/utils'
import Link from 'next/link'
import React from 'react'

import { useLanguageSwitch } from '../../../../hooks/use-language-switch'
import { useLocale } from '../../../../hooks/use-locale'
import { cn } from '../../../../shadcn/utils'
import { setCookieClient } from '../../../../utils/cookies'

type LanguageLinkProps = {
  locale: Locale
  labels: {
    language: string
    country: string
  }
  pathname: string
  searchParams: string
}

export function LanguageMenu({
  pathname,
  searchParams,
}: {
  pathname: string
  searchParams: string
}) {
  const languages = Object.entries(langParams).map(([key, value]) => ({
    locale: key as Locale,
    labels: {
      language: value.langLabel,
      country: value.location.label,
    },
  }))

  return (
    <nav>
      <ul className='grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4'>
        {languages.map((language) => (
          <li key={language.locale}>
            <LanguageLink
              pathname={pathname}
              searchParams={searchParams}
              {...language}
            />
          </li>
        ))}
      </ul>
    </nav>
  )
}

function LanguageLink({
  locale: targetLocale,
  labels,
  pathname,
  searchParams,
}: LanguageLinkProps) {
  const { locale: currentLocale } = useLocale()
  const { handleClose, onLocaleChange, resolveTranslatedPath } =
    useLanguageSwitch()
  const asPath = searchParams.toString()
    ? `${pathname}?${searchParams}`
    : pathname

  const isSelected = currentLocale === targetLocale
  const pathResult = resolveTranslatedPath({
    route: pathname,
    asPath,
    currentLocale,
    targetLocale,
  })
  const resolvedPathname = {
    resolved: !!pathResult,
    value: pathResult ?? asPath,
  }

  const handleLocaleChange = async (
    event: React.SyntheticEvent<HTMLAnchorElement>,
  ) => {
    if (isSelected) {
      return
    }

    event.preventDefault()
    setCookieClient('NEXT_LOCALE', targetLocale)
    onLocaleChange(targetLocale)
    handleClose()

    // Force reload to ensure middleware reads the updated cookie
    window.location.reload()
  }

  const Tag = isSelected ? 'span' : Link

  return (
    <Tag
      title={labels.language}
      href={resolvedPathname.value}
      className={cn(
        'flex flex-col gap-2 rounded-md border px-4 py-2 text-sm leading-tight text-muted-foreground',
        isSelected
          ? 'pointer-events-none border-grey-400'
          : 'border-transparent transition-colors hover:bg-muted',
      )}
      onClick={handleLocaleChange}
      locale={targetLocale}
    >
      <span className='text-base font-medium text-foreground'>
        {labels.country}
      </span>
      {labels.language}
    </Tag>
  )
}
